/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import * as RelationshipActionCreators from '@app/actions/RelationshipActionCreators';
import * as TextCopyActionCreators from '@app/actions/TextCopyActionCreators';
import {BanMemberModal} from '@app/components/modals/BanMemberModal';
import {ChangeNicknameModal} from '@app/components/modals/ChangeNicknameModal';
import {ConfirmModal} from '@app/components/modals/ConfirmModal';
import type {IARContext} from '@app/components/modals/IARModal';
import {IARModal} from '@app/components/modals/IARModal';
import {KickMemberModal} from '@app/components/modals/KickMemberModal';
import {RemoveTimeoutModal} from '@app/components/modals/RemoveTimeoutModal';
import {TimeoutMemberSheet} from '@app/components/modals/TimeoutMemberSheet';
import {TransferOwnershipModal} from '@app/components/modals/TransferOwnershipModal';
import styles from '@app/components/modals/UserProfileActionsSheet.module.css';
import {MenuBottomSheet, type MenuGroupType} from '@app/components/uikit/menu_bottom_sheet/MenuBottomSheet';
import {useRoleHierarchy} from '@app/hooks/useRoleHierarchy';
import type {GuildMemberRecord} from '@app/records/GuildMemberRecord';
import type {UserRecord} from '@app/records/UserRecord';
import AuthenticationStore from '@app/stores/AuthenticationStore';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import GuildStore from '@app/stores/GuildStore';
import PermissionStore from '@app/stores/PermissionStore';
import RelationshipStore from '@app/stores/RelationshipStore';
import {
	resolveGuildModerationCapabilities,
	resolveGuildScopedModerationActionKeys,
} from '@app/utils/GuildModerationCapabilityUtils';
import * as PermissionUtils from '@app/utils/PermissionUtils';
import {canSubmitReport, showReportRestrictionDialog} from '@app/utils/ReportVerificationGate';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {RelationshipTypes} from '@fluxer/constants/src/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {
	ClockIcon,
	CopyIcon,
	CrownIcon,
	FlagIcon,
	GavelIcon,
	GlobeIcon,
	IdentificationCardIcon,
	PencilIcon,
	ProhibitIcon,
	SignOutIcon,
	UserMinusIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

interface UserProfileActionsSheetProps {
	isOpen: boolean;
	onClose: () => void;
	user: UserRecord;
	isCurrentUser?: boolean;
	hasGuildProfile?: boolean;
	showGlobalProfile?: boolean;
	onToggleProfileView?: () => void;
	guildId?: string;
	guildMember?: GuildMemberRecord | null;
}

export const UserProfileActionsSheet: React.FC<UserProfileActionsSheetProps> = observer(
	({
		isOpen,
		onClose,
		user,
		isCurrentUser = false,
		hasGuildProfile = false,
		showGlobalProfile = false,
		onToggleProfileView,
		guildId,
		guildMember,
	}) => {
		const {t, i18n} = useLingui();
		const relationshipType = RelationshipStore.getRelationship(user.id)?.type;

		const guild = guildId ? GuildStore.getGuild(guildId) : null;
		const member = guildMember ?? (guildId ? GuildMemberStore.getMember(guildId, user.id) : null);
		const currentUserId = AuthenticationStore.currentUserId;

		const canKickMembers = guildId ? PermissionStore.can(Permissions.KICK_MEMBERS, {guildId}) : false;
		const canBanMembers = guildId ? PermissionStore.can(Permissions.BAN_MEMBERS, {guildId}) : false;
		const canModerateMembers = guildId ? PermissionStore.can(Permissions.MODERATE_MEMBERS, {guildId}) : false;
		const hasChangeNicknamePermission = guildId ? PermissionStore.can(Permissions.CHANGE_NICKNAME, {guildId}) : false;
		const hasManageNicknamesPermission = guildId ? PermissionStore.can(Permissions.MANAGE_NICKNAMES, {guildId}) : false;
		const isOwner = guild?.ownerId === currentUserId;
		const {canManageTarget} = useRoleHierarchy(guild);
		const guildSnapshot = guild?.toJSON();
		const targetHasModerateMembersPermission =
			guildSnapshot !== undefined && PermissionUtils.can(Permissions.MODERATE_MEMBERS, user.id, guildSnapshot);
		const {canKick, canBan, canTimeout} = resolveGuildModerationCapabilities({
			isCurrentUser,
			canManageTarget: canManageTarget(user.id),
			canKickMembers,
			canBanMembers,
			canModerateMembers,
			targetHasModerateMembersPermission,
		});
		const canTransfer = !isCurrentUser && isOwner && member;
		const canManageNicknames =
			member &&
			((isCurrentUser && hasChangeNicknamePermission) || (hasManageNicknamesPermission && canManageTarget(user.id)));
		const memberIsTimedOut = member?.isTimedOut() ?? false;

		const handleCopyFluxerTag = () => {
			TextCopyActionCreators.copy(i18n, `${user.username}#${user.discriminator}`, true);
			onClose();
		};

		const handleCopyUserId = () => {
			TextCopyActionCreators.copy(i18n, user.id, true);
			onClose();
		};

		const handleRemoveFriend = () => {
			onClose();
			ModalActionCreators.push(
				modal(() => (
					<ConfirmModal
						title={t`Remove Friend`}
						description={t`Are you sure you want to remove ${user.username} as a friend?`}
						primaryText={t`Remove Friend`}
						primaryVariant="danger-primary"
						onPrimary={async () => {
							RelationshipActionCreators.removeRelationship(user.id);
						}}
					/>
				)),
			);
		};

		const handleBlockUser = () => {
			onClose();
			ModalActionCreators.push(
				modal(() => (
					<ConfirmModal
						title={t`Block User`}
						description={t`Are you sure you want to block ${user.username}? They won't be able to message you or send you friend requests.`}
						primaryText={t`Block`}
						primaryVariant="danger-primary"
						onPrimary={async () => {
							RelationshipActionCreators.blockUser(user.id);
						}}
					/>
				)),
			);
		};

		const handleUnblockUser = () => {
			onClose();
			ModalActionCreators.push(
				modal(() => (
					<ConfirmModal
						title={t`Unblock User`}
						description={t`Are you sure you want to unblock ${user.username}?`}
						primaryText={t`Unblock`}
						primaryVariant="primary"
						onPrimary={async () => {
							RelationshipActionCreators.removeRelationship(user.id);
						}}
					/>
				)),
			);
		};

		const handleChangeNickname = () => {
			if (!guildId || !member) return;
			onClose();
			ModalActionCreators.push(modal(() => <ChangeNicknameModal guildId={guildId} user={user} member={member} />));
		};

		const handleKickMember = () => {
			if (!guildId) return;
			onClose();
			ModalActionCreators.push(modal(() => <KickMemberModal guildId={guildId} targetUser={user} />));
		};

		const handleBanMember = () => {
			if (!guildId) return;
			onClose();
			ModalActionCreators.push(modal(() => <BanMemberModal guildId={guildId} targetUser={user} />));
		};

		const handleTimeoutMember = () => {
			if (!guildId || !member) return;
			onClose();
			ModalActionCreators.push(
				modal(() => (
					<TimeoutMemberSheet
						isOpen={true}
						onClose={() => ModalActionCreators.pop()}
						guildId={guildId}
						targetUser={user}
						targetMember={member}
					/>
				)),
			);
		};

		const handleRemoveTimeout = () => {
			if (!guildId) return;
			onClose();
			ModalActionCreators.push(modal(() => <RemoveTimeoutModal guildId={guildId} targetUser={user} />));
		};

		const handleTransferOwnership = () => {
			if (!guildId || !member) return;
			onClose();
			ModalActionCreators.push(
				modal(() => <TransferOwnershipModal guildId={guildId} targetUser={user} targetMember={member} />),
			);
		};

		const handleReportUser = () => {
			onClose();
			if (!canSubmitReport()) {
				showReportRestrictionDialog();
				return;
			}
			const context: IARContext = {
				type: 'user',
				user,
				guildId,
			};
			ModalActionCreators.push(modal(() => <IARModal context={context} />));
		};

		const menuGroups: Array<MenuGroupType> = [];

		if (hasGuildProfile && onToggleProfileView) {
			menuGroups.push({
				items: [
					{
						icon: <GlobeIcon className={styles.icon} />,
						label: showGlobalProfile ? t`View Community Profile` : t`View Global Profile`,
						onClick: () => {
							onToggleProfileView();
							onClose();
						},
					},
				],
			});
		}

		menuGroups.push({
			items: [
				{
					icon: <CopyIcon className={styles.icon} />,
					label: t`Copy FluxerTag`,
					onClick: handleCopyFluxerTag,
				},
				{
					icon: <IdentificationCardIcon className={styles.icon} />,
					label: t`Copy User ID`,
					onClick: handleCopyUserId,
				},
			],
		});

		if (guildId) {
			const guildItems = [];

			if (member && canManageNicknames) {
				guildItems.push({
					icon: <PencilIcon className={styles.icon} />,
					label: isCurrentUser ? t`Change Nickname` : t`Change Nickname`,
					onClick: handleChangeNickname,
				});
			}

			if (guildItems.length > 0) {
				menuGroups.push({items: guildItems});
			}

			if (member && canTransfer) {
				menuGroups.push({
					items: [
						{
							icon: <CrownIcon className={styles.icon} />,
							label: t`Transfer Ownership`,
							onClick: handleTransferOwnership,
							danger: true,
						},
					],
				});
			}

			const moderationActionKeys = resolveGuildScopedModerationActionKeys({
				hasMember: member != null,
				canTimeout,
				isTimedOut: memberIsTimedOut,
				canKick,
				canBan,
			});

			if (moderationActionKeys.length > 0) {
				const moderationItems = [];

				for (const actionKey of moderationActionKeys) {
					switch (actionKey) {
						case 'timeout':
							moderationItems.push({
								icon: <ClockIcon className={styles.icon} />,
								label: t`Timeout`,
								onClick: handleTimeoutMember,
								danger: true,
							});
							break;
						case 'remove_timeout':
							moderationItems.push({
								icon: <ClockIcon className={styles.icon} />,
								label: t`Remove Timeout`,
								onClick: handleRemoveTimeout,
								danger: true,
							});
							break;
						case 'kick':
							moderationItems.push({
								icon: <SignOutIcon className={styles.icon} />,
								label: t`Kick`,
								onClick: handleKickMember,
								danger: true,
							});
							break;
						case 'ban':
							moderationItems.push({
								icon: <GavelIcon className={styles.icon} />,
								label: t`Ban`,
								onClick: handleBanMember,
								danger: true,
							});
							break;
					}
				}

				menuGroups.push({items: moderationItems});
			}
		}

		if (!isCurrentUser) {
			if (relationshipType === RelationshipTypes.FRIEND) {
				menuGroups.push({
					items: [
						{
							icon: <UserMinusIcon className={styles.icon} />,
							label: t`Remove Friend`,
							onClick: handleRemoveFriend,
							danger: true,
						},
					],
				});
			}

			const reportBlockItems = [
				{
					icon: <FlagIcon className={styles.icon} />,
					label: t`Report User`,
					onClick: handleReportUser,
					danger: true,
				},
			];

			if (!user.system) {
				if (relationshipType !== RelationshipTypes.BLOCKED) {
					reportBlockItems.push({
						icon: <ProhibitIcon className={styles.icon} />,
						label: t`Block`,
						onClick: handleBlockUser,
						danger: true,
					});
				} else {
					reportBlockItems.push({
						icon: <ProhibitIcon className={styles.icon} />,
						label: t`Unblock`,
						onClick: handleUnblockUser,
						danger: false,
					});
				}
			}

			menuGroups.push({items: reportBlockItems});
		}

		return <MenuBottomSheet isOpen={isOpen} onClose={onClose} groups={menuGroups} />;
	},
);
